import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { adsApi } from '@/api/client';
import {
  buildLinkedCampaignIdSet,
  normalizeCampaignId,
  type BudgetRuleAssociatedCampaign,
} from '@/utils/budgetRuleCampaigns';
import { CampaignSearchPicker } from '@/components/CampaignSearchPicker';
import { useBudgetRuleAssociatedCampaigns } from '@/hooks/useBudgetRuleAssociatedCampaigns';
import type { CampaignAdProduct, SpCampaignNormalized } from '@/types/spCampaigns';
import { campaignAdProductToBudgetRuleApiProduct } from '@/types/spCampaigns';

type BudgetRuleCampaignAssociationsProps = {
  profileId: string;
  product: CampaignAdProduct;
  ruleId: string;
  profileCampaigns: SpCampaignNormalized[];
  editable?: boolean;
  onAssociationsChanged?: () => void;
  /** When provided, skips internal fetch (e.g. parent already loaded associations). */
  associated?: BudgetRuleAssociatedCampaign[];
  isLoading?: boolean;
  loadError?: Error | null;
  /** Always excluded from the add dropdown (e.g. current campaign on details page). */
  excludeCampaignIds?: string[];
};

export function BudgetRuleCampaignAssociations({
  profileId,
  product,
  ruleId,
  profileCampaigns,
  editable = true,
  onAssociationsChanged,
  associated: associatedProp,
  isLoading: isLoadingProp,
  loadError: loadErrorProp,
  excludeCampaignIds = [],
}: BudgetRuleCampaignAssociationsProps) {
  const qc = useQueryClient();
  const apiProduct = campaignAdProductToBudgetRuleApiProduct(product);
  const [addCampaignId, setAddCampaignId] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [associating, setAssociating] = useState(false);
  const [removingCampaignId, setRemovingCampaignId] = useState<string | null>(null);

  const useInternalQuery = associatedProp === undefined;
  const internal = useBudgetRuleAssociatedCampaigns(
    profileId,
    product,
    ruleId,
    useInternalQuery
  );

  const queryKey = internal.queryKey;
  const isLoading = useInternalQuery ? internal.isLoading : (isLoadingProp ?? false);
  const loadError = useInternalQuery ? internal.error : loadErrorProp;
  const associated = useInternalQuery ? internal.associated : (associatedProp ?? []);

  const linkedCampaignIds = useMemo(
    () => buildLinkedCampaignIdSet(associated, excludeCampaignIds),
    [associated, excludeCampaignIds]
  );

  const productCampaigns = useMemo(
    () => profileCampaigns.filter((c) => c.adProduct === product),
    [profileCampaigns, product]
  );

  const availableToAdd = useMemo(
    () =>
      productCampaigns.filter(
        (c) => !linkedCampaignIds.has(normalizeCampaignId(c.campaignId))
      ),
    [productCampaigns, linkedCampaignIds]
  );

  useEffect(() => {
    if (
      addCampaignId &&
      linkedCampaignIds.has(normalizeCampaignId(addCampaignId))
    ) {
      setAddCampaignId('');
    }
  }, [addCampaignId, linkedCampaignIds]);

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey });
    onAssociationsChanged?.();
  };

  const handleAssociate = async () => {
    const cid = addCampaignId.trim();
    if (!cid) return;
    setActionError(null);
    setAssociating(true);
    try {
      const res = await adsApi.associateBudgetRulesToCampaign(
        profileId,
        apiProduct,
        cid,
        [ruleId]
      );
      if (!res.success) throw new Error('Failed to link campaign');
      setAddCampaignId('');
      await invalidate();
    } catch (err) {
      setActionError((err as Error).message || 'Failed to link campaign');
    } finally {
      setAssociating(false);
    }
  };

  const handleDisassociate = async (campaignId: string) => {
    setActionError(null);
    setRemovingCampaignId(campaignId);
    try {
      const res = await adsApi.disassociateBudgetRuleFromCampaign(
        profileId,
        apiProduct,
        campaignId,
        ruleId
      );
      if (!res.success) throw new Error('Failed to unlink campaign');
      await invalidate();
    } catch (err) {
      setActionError((err as Error).message || 'Failed to unlink campaign');
    } finally {
      setRemovingCampaignId(null);
    }
  };

  const resolveCampaignName = (row: BudgetRuleAssociatedCampaign) => {
    if (row.campaignName) return row.campaignName;
    const match = productCampaigns.find((c) => c.campaignId === row.campaignId);
    return match?.name ?? row.campaignId;
  };

  if (!editable) {
    return null;
  }

  return (
    <div className="space-y-2 pt-2 border-t border-chrome-divider">
      <span className="text-foreground-subtle text-xs uppercase tracking-wide">Manage campaign links</span>

      {isLoading ? (
        <p className="text-xs text-foreground-subtle">Refreshing links…</p>
      ) : loadError ? (
        <p className="text-xs text-error">
          {(loadError as Error).message || 'Failed to load linked campaigns'}
        </p>
      ) : associated.length > 0 ? (
        <ul className="space-y-1.5 list-none p-0 m-0">
          {associated.map((row) => (
            <li
              key={row.campaignId}
              className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-chrome-divider bg-page/80 px-2.5 py-1.5"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {resolveCampaignName(row)}
                </p>
                <p className="text-xs text-foreground-subtle font-mono truncate">{row.campaignId}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {row.ruleStatus ? (
                  <span className="text-xs px-2 py-0.5 rounded border border-border bg-surface text-foreground-muted">
                    {row.ruleStatus}
                  </span>
                ) : null}
                <button
                  type="button"
                  onClick={() => handleDisassociate(row.campaignId)}
                  disabled={removingCampaignId === row.campaignId}
                  className="text-xs text-error-ink hover:underline disabled:opacity-50"
                >
                  {removingCampaignId === row.campaignId ? '…' : 'Remove'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="space-y-2 pt-1">
        <label className="block text-xs text-foreground-muted">Add campaign</label>
        <CampaignSearchPicker
          options={availableToAdd}
          value={addCampaignId}
          onChange={setAddCampaignId}
          disabled={associating}
          loading={isLoading}
          loadingMessage="Loading linked campaigns…"
        />
        <button
          type="button"
          onClick={handleAssociate}
          disabled={associating || !addCampaignId}
          className="px-3 py-1.5 text-xs font-medium rounded-pill border border-brand/30 text-brand hover:bg-brand-subtle disabled:opacity-50"
        >
          {associating ? 'Linking…' : 'Link campaign'}
        </button>
      </div>

      {actionError ? <p className="text-xs text-error">{actionError}</p> : null}
    </div>
  );
}
